//#region IMPORT
import React, { useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Typography,
  InputAdornment,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import { Dialog, Button } from '../../../../Components/MUIs';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import BusinessCommon from '../../../../Components/BusinessCommon/BusinessCommon';
import { CreateColumn } from '../../../../Services/agGrid/createColumn';
import { useDispatch } from 'react-redux';
import Commons from '../../../../Services/Common/Common';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { resetAddButtonError } from '../../../../Reducer/StatusFlagsReducer';
import { getColumnDefs2 } from './SAP_BASIC_02_ColumnDefs';
//#endregion

function SAP_BASIC_02_EvaluationModal(props) {
  // 그리드 참조
  const dispatch = useDispatch();
  const isReadOnly = props.isEvaluationReadOnly === true;
  const evaluationPosition =
    props.evaluationDetailData?.position ||
    props.selectedRowData?.[0]?.position ||
    '';

  const SyncEvaluationItemsChange = (updatedItems) => {
    if (isReadOnly) return;

    props.setEvaluationDetailData((prev) => ({
      ...prev,
      evaluationItems: updatedItems,
    }));

    if (!props.updateCurrentRowData1) return;

    const api = props.gridRef2?.current?.api;
    if (!api || props.currentRowIndexRef2?.current === null) return;

    const rowNode = api.getDisplayedRowAtIndex(props.currentRowIndexRef2.current);
    if (!rowNode) return;

    const updatedRowData = {
      ...rowNode.data,
      evaluationItems: updatedItems,
      editYn: 'Y',
    };
    rowNode.setData(updatedRowData);

    props.setChangedModalRowData2?.((prev) => {
      const filtered = (prev || []).filter((row) => row.no !== updatedRowData.no);
      return [...filtered, updatedRowData];
    });
  };

  const selectedEvaluationItems = Array.isArray(props.selectedEvaluationItem)
    ? props.selectedEvaluationItem
    : props.selectedEvaluationItem
      ? [props.selectedEvaluationItem]
      : [];
  // 왼쪽 그리드용 columnDefs
  const columnDefs1 = useMemo(
    () => getColumnDefs2(props),
    [
      props.DeleteNewRow1,
      props.RestoreRow1,
      props.gridRef2,
      props.evaluationListData,
      props.selectedRowNosRef2,
      props.lastCheckedIndexRef2,
      props.HandleRowSelection2,
    ]
  );

  // 오른쪽 폼 데이터 변환
  const tableData = useMemo(() => {
    const evaluationItems = props.evaluationDetailData?.evaluationItems || [];

    const evaluationTableRows = [
      {
        cells: [
          {
            content: '담당업무 항목',
            align: 'center',
            sx: {
              backgroundColor: '#f5f5f5',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'rgba(0, 0, 0, 0.6)',
            },
            colSpan: 5,
          },
          {
            content: '평가 등급',
            align: 'center',
            sx: {
              backgroundColor: '#f5f5f5',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'rgba(0, 0, 0, 0.6)',
              width: '160px',
            },
            colSpan: 2,
          },
        ],
        sx: { backgroundColor: '#f5f5f5' },
      },
      ...evaluationItems.map((item, itemIndex) => ({
        cells: [
          {
            type: 'input',
            gubun: 'I',
            value: item.name || '',
            disabled: isReadOnly,
            onChange: (e) => {
              if (isReadOnly) return;
              const nextText = String(e?.target?.value ?? '').trimStart();
              const updatedItems =
                props.evaluationDetailData.evaluationItems.map((i, idx) =>
                  idx === itemIndex
                    ? props.ResolveEvaluationItemDefaults
                      ? props.ResolveEvaluationItemDefaults(
                          nextText,
                          evaluationPosition,
                          props.codes,
                          i
                        )
                      : {
                          ...i,
                          name: nextText,
                          code: nextText,
                          evaComment: nextText,
                          grade: i.grade || '보통',
                        }
                    : i
                );

              SyncEvaluationItemsChange(updatedItems);
            },
            align: 'left',
            colSpan: 5,
          },
          {
            type: 'combo',
            gubun: 'I',
            value: item.grade || '보통',
            disabled: isReadOnly,
            items: [
              { value: '우수', name: '우수' },
              { value: '보통', name: '보통' },
              {
                value: '미흡',
                name: '미흡',
              },
            ],
            onChange: (e) => {
              if (isReadOnly) return;
              const updatedItems =
                props.evaluationDetailData.evaluationItems.map((i, idx) =>
                  idx === itemIndex
                    ? {
                        ...i,
                        grade: e,
                      }
                    : i
                );

              SyncEvaluationItemsChange(updatedItems);
            },
            sx: {
              fontSize: '0.875rem',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
            },
            align: 'center',
            colSpan: 3,
          },
        ],
        sx: {
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      })),
    ];

    return [
      {
        cells: [
          {
            content: '평가 기간',
            align: 'start',
            sx: {
              fontSize: '0.75rem',
              fontWeight: 500,
              color: 'rgba(0, 0, 0, 0.6)',
              backgroundColor: 'rgb(250, 250, 250)',
            },
            colSpan: 3,
          },
          {
            type: 'datePicker',
            gubun: 'I',
            disabled: isReadOnly,
            value: dayjs(
              props.evaluationDetailData?.evaStartDate ||
                dayjs().format('YYYY-MM-DD')
            ),
            onChange: (newValue) => {
              if (isReadOnly) return;
              let changeDate = dayjs(newValue).format('YYYY-MM-DD');
              props.setEvaluationDetailData((prev) => ({
                ...prev,
                evaStartDate: changeDate,
              }));
              // 현재 행의 evaStartDate도 업데이트
              if (props.updateCurrentRowData1) {
                props.updateCurrentRowData1('evaStartDate', changeDate);
              }
            },
            format: 'YYYY-MM-DD',
            slotProps: {
              textField: {
                size: 'small',
                fullWidth: true,
              },
            },
            align: 'start',
            sx: { flex: 1 },
          },
          {
            content: '~',
            align: 'center',
            sx: {
              color: 'rgba(0, 0, 0, 0.4)',
            },
          },
          {
            type: 'datePicker',
            gubun: 'I',
            disabled: isReadOnly,
            value: dayjs(
              props.evaluationDetailData?.evaEndDate ||
                dayjs().format('YYYY-MM-DD')
            ),
            onChange: (newValue) => {
              if (isReadOnly) return;
              let changeDate = dayjs(newValue).format('YYYY-MM-DD');
              props.setEvaluationDetailData((prev) => ({
                ...prev,
                evaEndDate: changeDate,
              }));
              // 현재 행의 evaEndDate도 업데이트
              if (props.updateCurrentRowData1) {
                props.updateCurrentRowData1('evaEndDate', changeDate);
              }
            },
            format: 'YYYY-MM-DD',
            slotProps: {
              textField: {
                size: 'small',
                fullWidth: true,
              },
            },
            align: 'start',
            sx: { flex: 1 },
          },
        ],
      },
      {
        cells: [
          {
            content: (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderBottom: '2px solid #1976d2',
                  pb: 1,
                  width: 'fit-content',
                }}
              >
                <FactCheckIcon sx={{ color: '#1976d2', fontSize: '20px' }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'rgba(0, 0, 0, 0.87)',
                  }}
                >
                  업무 이행 평가
                </Typography>
              </Box>
            ),
            colSpan: 6,
            align: 'start',
          },
        ],
      },
      ...evaluationTableRows,
      {
        cells: [
          {
            content: '종합 의견 (Summary Opinion)',
            align: 'start',
            sx: {
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              color: 'rgba(0, 0, 0, 0.6)',
              backgroundColor: 'rgb(250, 250, 250)',
            },
          },
          {
            type: 'input',
            gubun: 'I',
            disabled: isReadOnly,
            value: props.evaluationDetailData?.opinion || '',
            onChange: (e) => {
              if (isReadOnly) return;
              props.setEvaluationDetailData((prev) => ({
                ...prev,
                opinion: e.target.value,
              }));
              // 현재 행의 opinion도 업데이트
              if (props.updateCurrentRowData1) {
                props.updateCurrentRowData1('opinion', e.target.value);
              }
            },
            multiline: true,
            rows: 3,
            placeholder: '평가 대상자에 대한 전체적인 피드백을 입력하세요...',
            sx: {
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            },
            align: 'start',
            colSpan: 5,
          },
        ],
      },
      {
        cells: [
          {
            content: '향후 개선 사항 (Action Items)',
            align: 'start',
            sx: {
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              color: 'rgba(0, 0, 0, 0.6)',
              backgroundColor: 'rgb(250, 250, 250)',
            },
          },
          {
            type: 'input',
            gubun: 'I',
            disabled: isReadOnly,
            value: props.evaluationDetailData?.improvements || '',
            onChange: (e) => {
              if (isReadOnly) return;
              props.setEvaluationDetailData((prev) => ({
                ...prev,
                improvements: e.target.value,
              }));
              // 현재 행의 improvements도 업데이트
              if (props.updateCurrentRowData1) {
                props.updateCurrentRowData1('improvements', e.target.value);
              }
            },
            multiline: true,
            rows: 3,
            placeholder: '구체적인 개선 계획 및 지원 사항을 기술하세요...',
            sx: {
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            },
            align: 'start',
            colSpan: 5,
          },
        ],
      },
    ];
  }, [props.evaluationDetailData, isReadOnly, evaluationPosition, props.codes]);

  return (
    <Dialog
      PaperProps={{
        sx: {
          borderRadius: 2,
          '& .MuiDialogTitle-root': {
            backgroundColor: '#2CA900',
          },
        },
      }}
      fullScreen
      titleSx={{ p: 0 }}
      title={
        <AppBar
          sx={{
            color: '#fff',
            backgroundColor: '#2CA900',
            position: 'static',
            boxShadow: 'none',
          }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                props.setEvaluationModalToggle(false);
                props.setSelectedEvaluationData(null);
                props.setEvaluationListData([]);
                props.setSelectedEvaluationItem(null);
                dispatch(resetAddButtonError());
              }}
              aria-label="close"
              sx={{ marginRight: 2 }}
            >
              <CloseIcon />
            </IconButton>
            <Typography
              sx={{
                ml: 2,
                flex: 1,
                textAlign: 'left',
              }}
              variant="h6"
              component="div"
            >
              <Box
                sx={{
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'clip',
                  wordWrap: 'break-word',
                }}
              >
                {'평가표'}
              </Box>
            </Typography>
            <Button
              sx={{
                mr: 1,
                color: '#1976d2',
                borderColor: '#1976d2',
                backgroundColor: '#fff',
                fontWeight: 600,
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#fff',
                  backgroundColor: '#1976d2',
                  borderColor: '#1976d2',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                },
                '&.Mui-disabled': {
                  color: '#9e9e9e',
                  borderColor: '#e0e0e0',
                  backgroundColor: '#f5f5f5',
                },
              }}
              disabled={isReadOnly}
              name={
                <>
                  <SaveIcon fontSize={Commons.IsMobile() ? 'small' : ''} />
                  저장
                </>
              }
              onClick={() => {
                if (isReadOnly) {
                  dispatch(
                    showSnackbar({
                      message: '저장된 평가표는 수정할 수 없습니다.',
                      type: 'warning',
                    })
                  );
                  return;
                }
                props.Save2();
              }}
              variant="outlined"
            />
            <Button
              sx={{
                mr: 1,
                color: '#9C27B0',
                borderColor: '#9C27B0',
                backgroundColor: '#fff',
                fontWeight: 600,
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#fff',
                  backgroundColor: '#9C27B0',
                  borderColor: '#9C27B0',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px rgba(156, 39, 176, 0.3)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 2px 4px rgba(156, 39, 176, 0.2)',
                },
              }}
              name={
                <>
                  <PrintIcon fontSize={Commons.IsMobile() ? 'small' : ''} />
                  인쇄
                </>
              }
              onClick={(e) => {
                // ✅ 현재 그리드에서 newYn === 'Y'인 행이 있는지 확인
                const api = props.gridRef2?.current?.api;
                let hasNewRow = false;

                if (api) {
                  api.forEachNode((node) => {
                    if (node.data?.newYn === 'Y') {
                      hasNewRow = true;
                    }
                  });
                }

                if (hasNewRow) {
                  dispatch(
                    showSnackbar({
                      message:
                        '수정 중인 항목이 있습니다. 저장 후 인쇄해주세요.',
                      type: 'warning',
                    })
                  );
                  return;
                }
                if (selectedEvaluationItems.length === 0) {
                  dispatch(
                    showSnackbar({
                      message: '인쇄할 항목을 선택해주세요.',
                      type: 'warning',
                    })
                  );
                  return;
                }
                // else if (props.selectedEvaluationData.length > 1) {
                //   props.dispatch(
                //     props.showSnackbar({
                //       message: '하나의 항목만 인쇄 가능합니다.',
                //       type: 'warning',
                //     })
                //   );
                // }
                else {
                  selectedEvaluationItems.forEach((item) => {
                    props.PrintLogic2(item);
                  });
                }
              }}
              variant="outlined"
            />
          </Toolbar>
        </AppBar>
      }
      open={props.evaluationModalToggle}
      onClose={() => {
        props.setEvaluationModalToggle(false);
        props.setSelectedEvaluationData(null);
        props.setEvaluationListData([]);
        props.setSelectedEvaluationItem(null);
      }}
      contentSx={{ mx: 2 }}
      content={
        <div
          className="ag-theme-alpine no-scroll"
          style={{ height: '100%', width: '100%' }}
        >
          <BusinessCommon
            title1={'평가표 기본정보'}
            title2={'평가표 상세정보'}
            gridId={'grid1'}
            getRowId={(params) => {
              return params.data.no;
            }}
            leftSize={50}
            rightSize={50}
            gridRef={props.gridRef2}
            addClick1={() => props.HandleAddClick(props.gridRef2)}
            refreshClick1={() => {
              const row = props.selectedRowData?.[0];
              const bpmId = row?.bpmId ?? row?.BpmId ?? row?.BPM_ID ?? '';
              const bpdId = String(
                row?.bpdId ?? row?.BpdId ?? row?.BPD_ID ?? ''
              ).trim();
              const bpdiId = String(
                row?.bpdiId ?? row?.BpdiId ?? row?.BPDI_ID ?? ''
              ).trim();
              if (bpmId && bpdId !== '') {
                props.Search2({ bpmId, bpdId, bpdiId });
              }
            }}
            deleteClick1={(e) => {
              const newRowExists = selectedEvaluationItems.some(
                (row) => row.newYn === 'Y'
              );

              if (!newRowExists) {
                props.MarkedForDeletion1(selectedEvaluationItems);
              } else {
                props.NewYnErrorMsg();
              }
            }}
            pagination={false}
            columnDefs={columnDefs1}
            rowData={props.evaluationListData}
            setSelectedRowData={props.setSelectedEvaluationData}
            tableData={tableData}
            colWidth={props.colWidth1}
            setChangedRowData={props.setChangedModalRowData2}
            onCellValueChanged={(e) => {
              if (isReadOnly) return;
              const key = e.data.no;
              const updatedRowData = { ...e.data, editYn: 'Y' };

              // changedModalRowData2 업데이트
              if (props.setChangedModalRowData2) {
                props.setChangedModalRowData2((prev) => {
                  const filtered = prev.filter((row) => row.no !== key);
                  return [...filtered, updatedRowData];
                });
              }
            }}
            onCellClicked={(e) => {
              const gridApi = props.gridRef2.current.api;
              const clickedRowIndex = e.rowIndex;

              const isCheckboxClick = e.event?.target?.type === 'checkbox';

              if (!isCheckboxClick) {
                gridApi.ensureIndexVisible(clickedRowIndex, 'middle');
                // gridApi.setFocusedCell(clickedRowIndex, 'coNm'); // 해당 셀로 포커스 이동

                const copied = JSON.parse(JSON.stringify(e.data));
                props.HandleRowSelection2(copied, clickedRowIndex);

                gridApi
                  .getDisplayedRowAtIndex(clickedRowIndex)
                  ?.setSelected(true, false);
              }
            }}
            onSelectionChanged={() => {
              const gridApi = props.gridRef2?.current?.api;
              const selectedNodes = gridApi.getSelectedNodes();
              const selectedData = selectedNodes.map((n) => n.data);
              props.selectedRowNosRef2.current = new Set(
                selectedData.map((r) => r.no)
              );
              props.setSelectedEvaluationItem(selectedData);
            }}
          />
        </div>
      }
      useCloseButton={false}
      onSave={() => {}}
      maxWidth="sm"
    />
  );
}

export default SAP_BASIC_02_EvaluationModal;
